import { PARTICIPANT_KEYS } from "@/consts";
import converters from "@/converters";
import {
  Profile_Type,
  type AllParticipants_Employee_Fragment,
  type AllParticipants_Relative_Fragment,
  type AllParticipants_Resident_Fragment,
  type InputMaybe,
  type Scalars,
} from "@/graphql/generated-types";
import ChangeTracker, {
  type IChangeTracker,
} from "@/helpers/forms/ChangeTracker";
import type { IMultiSelectItem } from "@/interfaces";
import { sentryException } from "@/sentry";

export type State<T> = {
  data: T;
  tracker: IChangeTracker;
};

export type ParticipantFeatureState<T> = {
  participant: State<T>;
  participantList: IMultiSelectItem[];
};

type TypeNames = "Employee" | "Relative" | "Resident" | undefined;

type DataModel = {
  responsible?: Array<{
    __typename?: TypeNames;
    id: string;
    type?: Profile_Type;
  }> | null;
  participants?: Array<
    | AllParticipants_Employee_Fragment
    | AllParticipants_Relative_Fragment
    | AllParticipants_Resident_Fragment
  > | null;
};

type FormInput = {
  employeesResponsible?: InputMaybe<Array<Scalars["ID"]["input"]>>;
  employeesSignedUp?: InputMaybe<Array<Scalars["ID"]["input"]>>;
  residentsResponsible?: InputMaybe<Array<Scalars["ID"]["input"]>>;
  residentsSignedUp?: InputMaybe<Array<Scalars["ID"]["input"]>>;
};

type GetFreshData = () => Promise<DataModel | undefined>;

const toParticipantsData = <T>(keys: string[], data: DataModel): T => {
  const participants = data.participants || [];
  const responsible = data.responsible || [];

  return {
    employeesResponsible: keys.includes("employeesResponsible")
      ? responsible
          .filter((item) => item.__typename === "Employee")
          .map((item) => item.id)
          .toSorted()
      : undefined,

    employeesSignedUp: keys.includes("employeesSignedUp")
      ? participants
          .filter((item) => item.__typename === "Employee")
          .map((item) => item.id)
          .toSorted()
      : undefined,

    residentsResponsible: keys.includes("residentsResponsible")
      ? responsible
          .filter((item) => item.__typename === "Resident")
          .map((item) => item.id)
          .toSorted()
      : undefined,

    residentsSignedUp: keys.includes("residentsSignedUp")
      ? participants
          .filter((item) => item.__typename === "Resident")
          .map((item) => item.id)
          .toSorted()
      : undefined,
  } as T;
};

export const getParticipantCount = (
  state: ParticipantFeatureState<unknown>,
) => {
  const data: string[] = [];
  Object.keys(state.participant.data as Record<string, string[]>)
    .filter((key) =>
      (PARTICIPANT_KEYS.SIGNUP as readonly string[]).includes(key),
    )
    .forEach((key) => {
      data.push(
        ...((state.participant.data as Record<string, string[]>)[key] || []),
      );
    });
  return data.flat().length;
};

class ParticipantFeature<T> {
  constructor(private readonly keys: ReadonlyArray<keyof T & string>) {}

  getDefault(): ParticipantFeatureState<T> {
    const data: Partial<T> = {};

    this.keys.forEach((key) => {
      (data[key] as unknown) = [];
    });

    return {
      participant: {
        data: data as T,
        tracker: new ChangeTracker(data, [...this.keys]),
      },
      participantList: [],
    };
  }

  getItem(data: DataModel): ParticipantFeatureState<T> {
    const convertedData = toParticipantsData<T>([...this.keys], data);
    return {
      participant: {
        data: convertedData,
        tracker: new ChangeTracker(convertedData as Record<string, unknown>, [
          ...this.keys,
        ]),
      },
      participantList: (data.participants || []).map(
        converters.participants.toMultiSelectItem,
      ),
    };
  }

  append(input: FormInput, state: ParticipantFeatureState<T>) {
    this.keys.forEach((key) => {
      state.participant.tracker.appendIfChanged(input, key);
    });
  }

  async hasStaleData(
    state: ParticipantFeatureState<T>,
    getFreshData: GetFreshData,
  ) {
    // If there is no change, we shouldn't check stale data for participants
    if (state.participant.tracker.hasAnyChange() !== true) {
      return false;
    }

    const freshData = await getFreshData();
    if (!freshData) {
      sentryException(new Error("The selected item not found"));
      return;
    }

    const convertedData = toParticipantsData<T>([...this.keys], freshData);

    // Let's check if the initial version were different than the fresh version
    return state.participant.tracker.isStale(
      convertedData as Record<string, unknown>,
    );
  }
}

export default ParticipantFeature;
